import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/permissions';
import { UserRole } from '../../types';
import { projectService, personName, Project, ProjectCompany, ProjectUser } from '../../services/projectService';
import ProjectDashboardList from './ProjectDashboardList';
import ProjectDashboardBoard from './ProjectDashboardBoard';
import ProjectsTrialBanner from './ProjectsTrialBanner';
import { getProjectsAccess, PROJECTS_TRIAL_DAYS } from '../../utils/projectsAccess';
import {
  dashboardViewFromMode,
  getStoredProjectViewMode,
  modeFromDashboardView,
  setStoredProjectViewMode
} from '../../utils/projectViewPreferences';
import './Projects.css';

const toIsoDay = (value: Date | null) => {
  if (!value) return null;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initials = (user: ProjectUser) => {
  const letters = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.trim();
  return (letters || user.email?.[0] || '?').toUpperCase();
};

const ProjectsHome: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { can } = usePermissions();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isAgent = user?.role === UserRole.AGENT;
  const needsCompanyPicker = isSuperAdmin || isAgent;
  const canManage = can(Permission.MANAGE_PROJECTS);

  const [companies, setCompanies] = useState<ProjectCompany[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>(() =>
    dashboardViewFromMode(getStoredProjectViewMode(user?._id))
  );
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createCompanyId, setCreateCompanyId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [companyUsers, setCompanyUsers] = useState<ProjectUser[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [startingDemo, setStartingDemo] = useState(false);
  const [hidingModule, setHidingModule] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setView(dashboardViewFromMode(getStoredProjectViewMode(user?._id)));
  }, [user?._id]);

  const changeView = (next: 'grid' | 'list') => {
    setView(next);
    setStoredProjectViewMode(user?._id, modeFromDashboardView(next));
  };

  const selectedCompany = needsCompanyPicker
    ? companies.find((company) => company._id === companyId)
    : user?.company;
  const access = getProjectsAccess(selectedCompany);
  const showDemoGate = !needsCompanyPicker && user?.role === UserRole.CLIENT_ADMIN && !access.enabled;

  useEffect(() => {
    const boot = async () => {
      if (needsCompanyPicker) {
        const unlocked = await projectService.listUnlockedCompanies();
        setCompanies(unlocked);
        const homeId = user?.company?._id || '';
        if (isAgent) {
          const next = unlocked.some((company) => company._id === homeId)
            ? homeId
            : unlocked[0]?._id || '';
          setCompanyId(next);
        } else {
          const stored = localStorage.getItem('projectsCompanyId');
          const next = stored && unlocked.some((company) => company._id === stored)
            ? stored
            : unlocked[0]?._id || '';
          setCompanyId(next);
        }
      } else {
        setCompanyId(user?.company?._id || '');
      }
    };
    boot().catch((err) => setError(err.response?.data?.message || 'Could not load companies'));
  }, [needsCompanyPicker, isAgent, user?.company?._id]);

  useEffect(() => {
    if (showDemoGate) {
      setProjects([]);
      setLoading(false);
      return;
    }
    if (!companyId && needsCompanyPicker) {
      setProjects([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setError('');
        const data = await projectService.listProjects(needsCompanyPicker ? companyId : undefined, includeArchived);
        if (cancelled) return;
        setProjects(data);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Could not load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, includeArchived, needsCompanyPicker, showDemoGate]);

  useEffect(() => {
    if (!showCreate) return;
    const targetCompany = isSuperAdmin ? createCompanyId : companyId;
    if (!targetCompany && isSuperAdmin) {
      setCompanyUsers([]);
      return;
    }
    projectService.listCompanyUsers(isSuperAdmin ? targetCompany : undefined)
      .then((users) => setCompanyUsers(users))
      .catch(() => setCompanyUsers([]));
  }, [showCreate, createCompanyId, companyId, isSuperAdmin]);

  const visible = useMemo(() => {
    const scoped = includeArchived ? projects : projects.filter((project) => project.status === 'active');
    const query = search.trim().toLowerCase();
    if (!query) return scoped;
    return scoped.filter((project) => {
      const haystack = `${project.name} ${project.description || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [includeArchived, projects, search]);

  const filteredUsers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    return companyUsers.filter((person) => {
      if (person._id === user?._id) return false;
      if (!query) return true;
      const haystack = `${personName(person)} ${person.email}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [companyUsers, memberSearch, user?._id]);

  const openCreate = () => {
    setName('');
    setDescription('');
    setStartDate(null);
    setEndDate(null);
    setMembers([]);
    setMemberSearch('');
    setCreateCompanyId(companyId);
    setShowCreate(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setSaving(false);
  };

  const startDemo = async () => {
    try {
      setStartingDemo(true);
      setError('');
      await projectService.startDemo();
      await refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not start the Projects demo');
    } finally {
      setStartingDemo(false);
    }
  };

  const hideModule = async () => {
    try {
      setHidingModule(true);
      setError('');
      await projectService.hideModule();
      await refreshUser();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not hide Projects');
      setHidingModule(false);
    }
  };

  const companyLabel = (company: ProjectCompany) => {
    const companyAccess = getProjectsAccess(company);
    return companyAccess.kind === 'trial' ? `${company.name} (Demo)` : company.name;
  };

  const toggleMember = (userId: string) => {
    setMembers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const createProject = async () => {
    if (!name.trim()) return;
    const targetCompanyId = isSuperAdmin ? createCompanyId : companyId;
    if (isSuperAdmin && !targetCompanyId) {
      setError('Select a company');
      return;
    }
    try {
      setSaving(true);
      const created = await projectService.createProject({
        name: name.trim(),
        description,
        company: isSuperAdmin ? targetCompanyId : undefined,
        members,
        startDate: toIsoDay(startDate),
        endDate: toIsoDay(endDate)
      });
      closeCreate();
      if (isSuperAdmin && targetCompanyId) {
        setCompanyId(targetCompanyId);
        localStorage.setItem('projectsCompanyId', targetCompanyId);
      }
      navigate(`/projects/${created._id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create project');
      setSaving(false);
    }
  };

  return (
    <div className="projects-module">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">
            {showDemoGate
              ? 'Try the Projects module with your team'
              : 'Kanban boards for this company'}
          </p>
        </div>
        {canManage && !showDemoGate && (
          <Button
            label="New project"
            className="new-ticket-btn"
            disabled={isSuperAdmin && !companyId}
            onClick={openCreate}
          />
        )}
      </div>

      {showDemoGate ? (
        <div className="projects-demo">
          <i className="pi pi-th-large" />
          <span className="projects-demo-kicker">{PROJECTS_TRIAL_DAYS}-day demo</span>
          <h2>{access.kind === 'expired' ? 'Projects demo ended' : 'Try Projects for 7 days'}</h2>
          <p>
            {access.kind === 'expired'
              ? 'Your company already used the 7-day Projects demo. Contact Uptimio to unlock the module for your team.'
              : 'Start a 7-day demo for your company. Your team can create projects, boards, tasks, and comments. After 7 days the module locks unless Uptimio unlocks it.'}
          </p>
          {access.kind !== 'expired' && (
            <div className="projects-demo-actions">
              <Button
                label={startingDemo ? 'Starting…' : 'Start 7-day demo'}
                className="new-ticket-btn"
                onClick={startDemo}
                disabled={startingDemo || hidingModule}
              />
              <Button
                label={hidingModule ? 'Hiding…' : "Don't show this"}
                className="p-button-text"
                onClick={hideModule}
                disabled={startingDemo || hidingModule}
              />
            </div>
          )}
          {error && <p className="usage-error">{error}</p>}
        </div>
      ) : (
        <>
      <ProjectsTrialBanner company={selectedCompany} />
      <div className="projects-toolbar">
        <div className="projects-toolbar-left">
          {needsCompanyPicker && (!isAgent || companies.length > 1) && (
            <Dropdown
              value={companyId}
              options={companies.map((company) => ({ label: companyLabel(company), value: company._id }))}
              onChange={(e) => {
                setCompanyId(e.value);
                localStorage.setItem('projectsCompanyId', e.value);
              }}
              placeholder="Select company"
              className="w-full md:w-20rem"
              panelClassName="projects-dropdown-panel"
            />
          )}
          <div className="projects-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
            </svg>
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
            />
          </div>
        </div>
        <div className="projects-toolbar-right">
          <label className="notify-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <InputSwitch checked={includeArchived} onChange={(e) => setIncludeArchived(!!e.value)} />
            <span>Archived</span>
          </label>
          <div className="projects-view-toggle">
            <button type="button" className={view === 'grid' ? 'is-active' : ''} onClick={() => changeView('grid')}>
              Board
            </button>
            <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => changeView('list')}>
              List
            </button>
          </div>
        </div>
      </div>

      {error && <p className="usage-error">{error}</p>}
      {!loading && visible.length === 0 && (
        <div className="mobile-empty-state">
          <h3>No projects yet</h3>
          <p>
            {needsCompanyPicker && !companyId
              ? (isAgent ? 'No accessible companies have Projects enabled.' : 'Unlock Projects on a company first.')
              : 'Create a project to get started.'}
          </p>
        </div>
      )}

      {visible.length > 0 && (view === 'grid' ? (
        <ProjectDashboardBoard
          projects={visible}
          onProjectClick={(projectId) => navigate(`/projects/${projectId}`)}
        />
      ) : (
        <ProjectDashboardList
          projects={visible}
          onProjectClick={(projectId) => navigate(`/projects/${projectId}`)}
        />
      ))}
        </>
      )}

      {showCreate && (
        <div
          className="app-modal-overlay"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.p-datepicker, .p-dropdown-panel, .p-multiselect-panel')) return;
            closeCreate();
          }}
        >
          <div className="app-modal create-project-card" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header">
              <div className="app-modal-header-text">
                <h2>Create New Project</h2>
              </div>
              <button type="button" className="app-modal-close" onClick={closeCreate} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-form">
              {error && <p className="usage-error">{error}</p>}
              <div className="form-group">
                <label htmlFor="project-name">Project name</label>
                <InputText
                  id="project-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My project"
                />
              </div>
              <div className="form-group">
                <label htmlFor="project-description">Description</label>
                <InputTextarea
                  id="project-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  autoResize
                  placeholder="Project description"
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                {isSuperAdmin ? (
                  <Dropdown
                    value={createCompanyId}
                    options={companies.map((company) => ({ label: companyLabel(company), value: company._id }))}
                    onChange={(e) => {
                      setCreateCompanyId(e.value);
                      setMembers([]);
                    }}
                    placeholder="Select company"
                    className="w-full"
                    appendTo={document.body}
                    panelClassName="projects-dropdown-panel"
                  />
                ) : (
                  <InputText value={user?.company?.name || ''} disabled />
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start date</label>
                  <Calendar
                    value={startDate}
                    onChange={(e) => setStartDate(e.value as Date | null)}
                    dateFormat="mm/dd/yy"
                    placeholder="mm/dd/yyyy"
                    showButtonBar
                    appendTo={document.body}
                    baseZIndex={3500}
                    className="w-full"
                    panelClassName="projects-datepicker"
                  />
                </div>
                <div className="form-group">
                  <label>End date</label>
                  <Calendar
                    value={endDate}
                    onChange={(e) => setEndDate(e.value as Date | null)}
                    dateFormat="mm/dd/yy"
                    placeholder="mm/dd/yyyy"
                    showButtonBar
                    appendTo={document.body}
                    baseZIndex={3500}
                    className="w-full"
                    panelClassName="projects-datepicker"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Project members (company members only)</label>
                <p className="create-members-hint">You are already on this project. Add others if needed.</p>
                <div className="create-member-search">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
                  </svg>
                  <InputText
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search by name or email..."
                  />
                </div>
                <div className="create-member-list">
                  {filteredUsers.length === 0 && (
                    <p className="stage-empty" style={{ borderTop: 'none', padding: '0.75rem 0.25rem' }}>
                      {memberSearch.trim() ? 'No company members found' : 'No other company members'}
                    </p>
                  )}
                  {filteredUsers.map((person) => {
                    const selected = members.includes(person._id);
                    return (
                      <button
                        key={person._id}
                        type="button"
                        className={`create-member-row${selected ? ' is-selected' : ''}`}
                        onClick={() => toggleMember(person._id)}
                      >
                        <span className={`create-member-check${selected ? ' is-checked' : ''}`} />
                        <span className="list-avatar list-avatar-fallback">{initials(person)}</span>
                        <span className="create-member-copy">
                          {personName(person)} ({person.email})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="modal-actions create-project-actions">
                <button type="button" className="text-btn" onClick={closeCreate}>Cancel</button>
                <Button label={saving ? 'Creating…' : 'Create project'} onClick={createProject} disabled={saving || !name.trim()} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsHome;
